import React, { useRef, useState } from 'react';

import { TextField } from '@mui/material';

import { EmployeeUserOption } from '~/hooks/apis/users/useGetEmployeeUsers';

import ChatterMentionPopover from './ChatterMentionPopover';
import { useMentionSession } from './useMention';
import { findActiveMention } from './utils';

interface Props {
  id: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

export default function ChatterMentionInput({ id, value, placeholder, onChange }: Props) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [caret, setCaret] = useState(0);

  const found = findActiveMention(value, caret);

  const insertMention = (employee: EmployeeUserOption) => {
    if (!found) {
      return;
    }
    const inserted = `@${employee.full_name} `;
    const nextCaret = found.start + inserted.length;
    onChange(`${value.slice(0, found.start)}${inserted}${value.slice(caret)}`);
    setCaret(nextCaret);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const mention = useMentionSession(found && { id: String(found.start), query: found.query }, insertMention);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
    setCaret(event.target.selectionStart ?? event.target.value.length);
    mention.reopen();
  };

  const syncCaret = () => {
    const input = inputRef.current;
    if (input) {
      setCaret(input.selectionStart ?? input.value.length);
    }
  };

  return (
    <>
      <TextField
        ref={anchorRef}
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={mention.handleKey}
        onKeyUp={syncCaret}
        onClick={syncCaret}
        onBlur={mention.dismiss}
        inputRef={inputRef}
        slotProps={{
          htmlInput: {
            id,
            autoComplete: 'off',
            role: 'combobox',
            'aria-expanded': mention.isOpen,
            'aria-autocomplete': 'list',
          },
        }}
      />
      <ChatterMentionPopover
        isOpen={mention.isOpen}
        anchorEl={anchorRef.current}
        suggestions={mention.suggestions}
        highlightedIndex={mention.highlightedIndex}
        isError={mention.isError}
        onSelect={mention.selectEmployee}
      />
    </>
  );
}
